import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { FormField } from "@/components/ui/form-field";
import { TimeRangeRows } from "../components/TimeRangeRows";
import { Switch } from "@/components/ui/switch";
import { DayRangeField } from "../components/DayRangeField";
import { UploadMultiImages } from "@/components/ui/upload-multi-images";
import { UploadMultiFiles } from "@/components/ui/upload-multi-files";
import { Link, MapPin, Euro, Clock, CalendarRange, FileText, Image as ImageIcon, Type, Library, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ActivityActivity } from "../models/activity.model";
import { useI18n } from "@/hooks/useI18n";
import ActivityModalShell from "./ActivityModalShell";
import type { DayAxis } from "../models/day-axis";
import ActivityDetailView from "./ActivityDetailView";
import { LibraryPicker } from "../components/LibraryPicker";
import type { LibraryActivity } from "@/lib/library/models/library-activity.model";

type ActivityTab = 'details' | 'media' | 'documents' | 'library';


interface ActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (activity: Partial<ActivityActivity>) => void;
  icon?: React.ReactNode;
  initialData?: Partial<ActivityActivity>;
  itineraryId: string;
  // optional itinerary bounds (may be passed by caller)
  itineraryStartDate?: string | null;
  itineraryEndDate?: string | null;
  /** Asse dei giorni: sui template si sceglie "Giorno N" invece di una data. */
  axis?: DayAxis | null;
  defaultDate?: string;
  /** Nasconde la card data e la rende non obbligatoria (usato dalla Libreria). */
  hideDates?: boolean;
  /** Sola lettura: campi non interagibili, footer con solo "Chiudi" (usato dalla Libreria). */
  readOnly?: boolean;
  /** Il cliente ha gia' pagato: costo e "includi nel prezzo" sono consolidati. */
}

const ActivityModal = ({ isOpen, onClose, onSave, icon, initialData, itineraryId, itineraryStartDate, itineraryEndDate, axis, defaultDate, hideDates = false, readOnly = false }: ActivityModalProps) => {
  const { t } = useI18n();

  // Generate temporary UUID for new activities
  const [activityId] = useState<string>(() =>
    initialData?.id || crypto.randomUUID()
  );

  const [formData, setFormData] = useState<Partial<ActivityActivity>>({
    id: activityId,
    include_price: true,
    title: '',
    link: '',
    address: '',
    start_date: undefined,
    end_date: undefined,
    start_time: '',
    end_time: '',
    cost: 0,
    media: [],
    documentation: []
  });

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData({ ...initialData, id: activityId });
    }
  }, [initialData, activityId]);

  const [errors, setErrors] = useState<Record<string, boolean>>({});
  // Import da libreria in due tempi: si sceglie, si conferma, poi si torna ai dati.
  const [pendingImport, setPendingImport] = useState<LibraryActivity | null>(null);
  const [showValidationBanner, setShowValidationBanner] = useState(false);
  const [activeTab, setActiveTab] = useState<ActivityTab>('details');

  useEffect(() => {
    if (!isOpen) {
      setShowValidationBanner(false);
      setErrors({});
      setActiveTab('details');
      setPendingImport(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !initialData && defaultDate) {
      setFormData(prev => ({ ...prev, start_date: defaultDate, end_date: defaultDate }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const validateForm = (): Record<string, boolean> => {
    const requiredFields = hideDates
      ? ['title', 'address', 'start_time', 'end_time', 'cost']
      : ['title', 'address', 'start_date', 'end_date', 'start_time', 'end_time', 'cost'];
    const newErrors: Record<string, boolean> = {};

    requiredFields.forEach(field => {
      const value = formData[field as keyof ActivityActivity];
      if (field === 'cost') {
        newErrors[field] = value === null || value === undefined || (value as number) < 0;
      } else {
        newErrors[field] = !value || (typeof value === 'string' && value.trim() === '');
      }
    });

    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (!Object.values(newErrors).some(error => error)) {
      onSave({
        ...formData,
        type: 'activity' as const
      });
      onClose();
      return;
    }

    setShowValidationBanner(true);
    setActiveTab('details');
  };

  // derive min/max for date picker: prefer explicit props, then common fields on initialData
  const derivedMinDate: string | null =
    itineraryStartDate
    || (initialData as any)?.itineraryStartDate
    || (initialData as any)?.itinerary_start_date
    || (initialData as any)?.min_date
    || (initialData as any)?.minDate
    || null;

  const derivedMaxDate: string | null =
    itineraryEndDate
    || (initialData as any)?.itineraryEndDate
    || (initialData as any)?.itinerary_end_date
    || (initialData as any)?.max_date
    || (initialData as any)?.maxDate
    || null;

  const hasDetailsError = ['title', 'address', 'start_date', 'end_date', 'start_time', 'end_time', 'cost']
    .some(field => errors[field]);
  const requiredError = String(t('validation.fieldRequired'));

  // In modifica non si importa: il tab compare solo creando una nuova attività.
  const canImportFromLibrary = !initialData;

  const confirmImport = () => {
    if (!pendingImport) return;
    const imported = pendingImport.toItineraryActivity(formData.start_date || defaultDate);
    // L'id resta quello di questa modale: l'import precompila un inserimento nuovo,
    // non porta con sé l'identità dell'attività di libreria.
    setFormData({ ...(imported as Partial<ActivityActivity>), id: activityId });
    setPendingImport(null);
    setErrors({});
    setShowValidationBanner(false);
    setActiveTab('details');
  };

  if (readOnly) {
    return (
      <ActivityDetailView
        isOpen={isOpen}
        onClose={onClose}
        icon={icon}
        title={t('builder.activities.activity')}
        activity={formData}
      />
    );
  }

  return (
    <ActivityModalShell
      isOpen={isOpen}
      onClose={onClose}
      icon={icon}
      title={t('builder.activities.activity')}
      formId="activity-form"
      onSubmit={handleSubmit}
      showValidationBanner={showValidationBanner}
      activeTab={activeTab}
      onActiveTabChange={(value) => setActiveTab(value as ActivityTab)}
      tabs={[
        {
          key: 'details',
          label: t('modals.tabDetails'),
          icon: <Type className="h-3.5 w-3.5 shrink-0" />,
          hasError: hasDetailsError,
          content: (
            <div className="space-y-5">
              {/* Prima riga: a sinistra chi e dove, a destra quanto costa. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-4">
                  <FormField
                    id="title"
                    label={t('modals.activityTitle')}
                    required
                    error={errors.title ? requiredError : undefined}
                  >
                    <Input
                      id="title"
                      value={formData.title || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      placeholder={t('modals.activityTitle')}
                      className="h-9"
                      hasError={errors.title}
                    />
                  </FormField>

                  <FormField
                    id="address"
                    label={t('modals.address')}
                    icon={<MapPin className="h-3 w-3" />}
                    required
                    error={errors.address ? requiredError : undefined}
                  >
                    <Input
                      id="address"
                      value={formData.address || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                      placeholder={t('modals.address')}
                      className="h-9"
                      hasError={errors.address}
                    />
                  </FormField>
                </div>

                <div className="space-y-4">
                  <FormField
                    id="cost"
                    label={t('modals.price')}
                    icon={<Euro className="h-3 w-3" />}
                    required
                    hint={t('modals.costHint')}
                    error={errors.cost ? requiredError : undefined}
                  >
                    <Input
                      id="cost"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.cost}
                      onChange={(e) => setFormData(prev => ({ ...prev, cost: parseFloat(e.target.value) }))}
                      placeholder="0,00"
                      className="h-9 tabular-nums"
                      hasError={errors.cost}
                    />
                  </FormField>

                <div className="flex items-start justify-between gap-4 rounded-md bg-gray-50 px-3 py-2.5">
                  <div className="space-y-0.5">
                    <p className="text-xs text-gray-700">{t('modals.includePrice')}</p>
                    <p className="text-[11px] text-gray-500">{t('modals.includePriceHint')}</p>
                  </div>
                  <Switch
                    checked={formData.include_price || false}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, include_price: checked }))}
                  />
                </div>
                </div>
              </div>

              <FormField label={t('modals.description')}>
                <RichTextEditor
                  value={formData.description || ''}
                  onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                  placeholder={t('modals.describeActivity')}
                />
              </FormField>

              {/* Data, orario e prezzo sulla stessa riga; su mobile vanno a capo. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {!hideDates && (
                  <FormField
                    label={t('modals.dateRange')}
                    icon={<CalendarRange className="h-3 w-3" />}
                    required
                    error={errors.start_date || errors.end_date ? requiredError : undefined}
                  >
                    <DayRangeField
                      axis={axis}
                      value={formData}
                      onChange={(patch) => setFormData(prev => ({ ...prev, ...patch }))}
                      hasError={errors.start_date || errors.end_date}
                      minDate={derivedMinDate || undefined}
                      maxDate={derivedMaxDate || undefined}
                      variant="rows"
                    />
                  </FormField>
                )}

                <FormField
                  label={t('modals.time')}
                  icon={<Clock className="h-3 w-3" />}
                  required
                  error={errors.start_time || errors.end_time ? requiredError : undefined}
                >
                  <TimeRangeRows
                    rows={[
                      { id: 'start_time', label: String(t('common.from')) },
                      { id: 'end_time', label: String(t('common.to')) },
                    ]}
                    values={formData}
                    onChange={(patch) => setFormData(prev => ({ ...prev, ...patch }))}
                  />
                </FormField>

              </div>

            </div>
          ),
        },
        {
          key: 'media',
          optional: true,
          label: t('modals.tabMedia'),
          icon: <ImageIcon className="h-3.5 w-3.5 shrink-0" />,
          hasContent: (formData.media?.length || 0) > 0,
          content: (
            <FormField
              label={t('modals.media')}
              hint={t('modals.mediaHint')}
            >
              <UploadMultiImages
                images={formData.media || []}
                onImagesChange={(images) => setFormData(prev => ({ ...prev, media: images }))}
                label={t('modals.upload4Photos')}
                maxImages={10}
                itineraryId={itineraryId}
                activityId={activityId}
              />
            </FormField>
          ),
        },
        {
          key: 'documents',
          optional: true,
          label: t('modals.tabDocumentsAndLinks'),
          icon: <FileText className="h-3.5 w-3.5 shrink-0" />,
          hasContent: (formData.documentation?.length || 0) > 0 || !!formData.link,
          content: (
            <div className="space-y-5">
              <FormField
                label={t('modals.documentation')}
                hint={t('modals.documentationHint')}
              >
                <UploadMultiFiles
                  files={formData.documentation || []}
                  onFilesChange={(files) => setFormData(prev => ({ ...prev, documentation: files }))}
                  label={t('modals.uploadBookingDocumentation')}
                  maxFiles={5}
                  itineraryId={itineraryId}
                  activityId={activityId}
                />
              </FormField>

              <FormField
                id="link"
                label={t('modals.link')}
                icon={<Link className="h-3 w-3" />}
                hint={t('modals.addSupportingLink')}
              >
                <Input
                  id="link"
                  type="url"
                  value={formData.link || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, link: e.target.value }))}
                  placeholder="https://..."
                  className="h-9"
                  hasError={errors.link}
                />
              </FormField>
            </div>
          ),
        },
        {
          key: 'library',
          group: 'import' as const,
          optional: true,
          hidden: !canImportFromLibrary,
          label: t('library.tabImport'),
          icon: <Library className="h-3.5 w-3.5 shrink-0" />,
          content: (
            <div className="space-y-4">
              {pendingImport && (
                <div className="flex flex-col gap-2 rounded-md border border-amber-200 bg-amber-50 p-3">
                  <p className="flex items-center gap-2 text-xs font-medium text-amber-800">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    {t('library.importConfirmTitle', { title: pendingImport.title })}
                  </p>
                  <p className="text-[11px] text-amber-700">{t('library.importConfirmBody')}</p>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" onClick={confirmImport}>
                      {t('library.importConfirmAction')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setPendingImport(null)}
                    >
                      {t('common.cancel')}
                    </Button>
                  </div>
                </div>
              )}

              <LibraryPicker
                activityType="activity"
                enabled={isOpen && activeTab === 'library'}
                onSelect={setPendingImport}
                listClassName="max-h-[40vh]"
              />
            </div>
          ),
        },
      ]}
    />
  );
};

export default ActivityModal;
